import {
  Button,
  LoadingOverlay,
  Modal,
  Text,
  TextInput
} from "@marketplace/ui";
import { useAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { erfinaProductModalAtom } from "../../atoms/integration-product-modal-atom";
import { useErfinaProductForm } from "../../hooks/forms/use-erfina-product-form";

export const ErfinaProductModal = () => {
  const { t } = useTranslation("product", {
    keyPrefix: "integrationProductListPage.erfinaModal"
  });

  const [{ visible: opened, data: modalData }] = useAtom(
    erfinaProductModalAtom
  );
  const resetErfinaModal = useResetAtom(erfinaProductModalAtom);

  const productId = modalData?.lampu_product_id ?? "";
  const onClose = resetErfinaModal;

  const { formMethods, onSubmit, isPending, isLoadingDetail } =
    useErfinaProductForm({
      productId,
      onSuccess: onClose
    });

  const {
    register,
    formState: { errors },
    reset
  } = formMethods;

  // Reset form when modal closes
  useEffect(() => {
    if (!opened) {
      reset();
    }
  }, [opened, reset]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text type="heading" weight="semibold">
          {t("title")}
        </Text>
      }
      size={610}
      classNames={{
        header: "!p-6",
        body: "!p-6 !pt-0"
      }}
    >
      <LoadingOverlay visible={isLoadingDetail} />
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          {/* Product Family */}
          <TextInput
            {...register("product_family")}
            size="lg"
            label={t("labels.productFamily")}
            placeholder={t("placeholders.productFamily")}
            error={errors.product_family?.message}
            disabled={isPending}
          />

          {/* Product ID */}
          <TextInput
            {...register("lampu_product_id")}
            size="lg"
            label={t("labels.productId")}
            placeholder={t("placeholders.productId")}
            error={errors.lampu_product_id?.message}
            disabled={isPending}
            readOnly
          />

          {/* Product Code Debit */}
          <TextInput
            {...register("product_code_debit")}
            size="lg"
            label={t("labels.productCodeDebit")}
            placeholder={t("placeholders.productCodeDebit")}
            error={errors.product_code_debit?.message}
            disabled={isPending}
            maxLength={3}
          />

          {/* COA Debit */}
          <TextInput
            {...register("coa_debit")}
            size="lg"
            label={t("labels.coaDebit")}
            placeholder={t("placeholders.coaDebit")}
            error={errors.coa_debit?.message}
            disabled={isPending}
            maxLength={8}
          />

          {/* Product Code Credit */}
          <TextInput
            {...register("product_code_credit")}
            size="lg"
            label={t("labels.productCodeCredit")}
            placeholder={t("placeholders.productCodeCredit")}
            error={errors.product_code_credit?.message}
            disabled={isPending}
            maxLength={3}
          />

          {/* COA Credit */}
          <TextInput
            {...register("coa_credit")}
            size="lg"
            label={t("labels.coaCredit")}
            placeholder={t("placeholders.coaCredit")}
            error={errors.coa_credit?.message}
            disabled={isPending}
            maxLength={8}
          />

          {/* Action Buttons */}
          <div className="flex justify-end gap-x-[14px]">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isPending}
              className="w-[128px]"
            >
              {t("buttons.discard")}
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={isPending}
              className="w-[128px]"
            >
              {t("buttons.submit")}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
